import React, { useRef, useState, useEffect } from 'react'
import {
  FlatList,
  Dimensions,
  View,
  LayoutAnimation,
  Platform,
  ScrollView,
} from 'react-native'
import PropTypes from 'prop-types'

import VfText from '../../Components/VfText'
import RecommendedCard from '../../Components/RecommendedCard'
import FilterItem from '../../Components/FilterItem'
import ListItem from '../../Components/ListItem'
import { round, isNonEmptyArray } from '../../Utils'

import styles from './Catalogue.Styles'

import { useTheme } from '@vfgroup-oneplatform/foundation/Components/Themes'

const { height } = Dimensions.get('window')
const HEADER_ITEMS = [
  { id: 'recomendedItemsList' },
  { id: 'subcategoriesList' },
]

function Catalogue({
  data: {
    recommendations,
    subcategories,
  } = {},
  onOfferPress,
}) {
  const [activeFilter, setActiveFilter] = useState(0)
  const [withRecomended, setWithRecomended] = useState(false)
  const [items, setItems] = useState([...HEADER_ITEMS])

  const theme = useTheme()

  useEffect(() => {
    if (Array.isArray(subcategories)) {
      setItems(HEADER_ITEMS.concat(subcategories[activeFilter].items))
    }
  }, [subcategories, activeFilter])

  useEffect(() => {
    LayoutAnimation.configureNext(LayoutAnimation.Presets.easeInEaseOut)
    setWithRecomended(isNonEmptyArray(recommendations))
  }, [recommendations])

  const ref = useRef(null)
  const snapRef = useRef(true)
  const [sizes, setSizes] = useState({
    container: 0,
    header: 0,
  })
  const onHeaderLayout = ({ nativeEvent }) => {
    setSizes(prev => ({ ...prev, header: nativeEvent.layout.height }))
  }

  const scrollTo = (offset) => {
    ref.current?.scrollToOffset({ offset })
  }

  const temporaryDisableMomentum = () => {
    snapRef.current = false
    setTimeout(() => {
      snapRef.current = true
    }, 100)
  }

  const handleSnap = (nativeEvent) => {
    const offsetY = nativeEvent.contentOffset.y
    if (offsetY > 0 && offsetY < sizes.header && sizes.container - sizes.header > height && snapRef.current) {
      scrollTo(round(offsetY, 0, sizes.header))
      temporaryDisableMomentum()
    }
  }

  const onDragEnd = ({ nativeEvent }) => {
    const velocityY = nativeEvent.velocity.y
    if (velocityY === 0) {
      handleSnap(nativeEvent)
    }
  }

  const onMomentumEnd = ({ nativeEvent }) => {
    handleSnap(nativeEvent)
  }

  const recomendedKeys = (_, idx) => 'recomended_' + idx
  const renderRecomendedItem = ({ item }) => (
    <RecommendedCard
      {...item}
      onPress={() => onOfferPress(item)}
    />
  )

  const filterKeys = (_, idx) => 'filter_' + idx
  const renderFilterItem = ({ item, index }) => {
    return (
      <FilterItem
        {...item}
        isSelected={index === activeFilter}
        onPress={() => setActiveFilter(index)}
      />
    )
  }

  const renderHeader = () => (
    <View style={styles.header(theme)} onLayout={onHeaderLayout}>
      {withRecomended && <>
        <VfText i18nKey="catalogue_title_section_recommended" size="h3" weight="bold" style={styles.sectionTitle} color={theme.colors.textColorThree} />
        <FlatList
          horizontal
          showsHorizontalScrollIndicator={false}
          decelerationRate={0.8}
          snapToInterval={252}
          data={recommendations}
          fadingEdgeLength={100}
          keyExtractor={recomendedKeys}
          renderItem={renderRecomendedItem}
          contentContainerStyle={styles.recomendedContentContainer}
        />
      </>}
      <VfText i18nKey="catalogue_title_section_filters" size="h3" weight="bold" style={styles.filters(theme)} />
    </View>
  )

  const renderStickyHeader = () => (
    <View style={styles.header(theme)}>
      <FlatList
        horizontal
        showsHorizontalScrollIndicator={false}
        decelerationRate={0.8}
        data={subcategories}
        keyExtractor={filterKeys}
        renderItem={renderFilterItem}
        contentContainerStyle={styles.filtersContentContainer}
      />
      <View style={styles.divider(theme)} />
    </View>
  )

  const itemKeys = (_, idx) => 'item_' + idx
  const renderItem = ({ item, index }) => {
    if (index > 1) {
      return (
        <ListItem
          {...item}
          onPress={() => onOfferPress(item)}
        />
      )
    }
    if (index === 0) {
      return renderHeader()
    }
    if (index === 1) {
      return renderStickyHeader()
    }
    return null
  }

  const onContentSizeChange = (_, h) => {
    setSizes(prev => ({ ...prev, container: h }))
  }


  if (Platform.OS === 'web' && Dimensions.get('window').width > 700) {
    return (
      <View style={{ flex: 1, overflow: 'scroll' }}>
        {renderHeader()}
        {renderStickyHeader()}
        <ScrollView
          contentContainerStyle={{ flexDirection: 'row', flexWrap: 'wrap', justifyContent: 'space-between', paddingBottom: 200, flexShrink: 0 }}
          style={{ flexShrink: 0 }}
        >
          {items.map((item, index) => {
            if (index < 2) return null
            else return (
              <ListItem
                {...item}
                onPress={() => onOfferPress(item)}
                isLast={index == items.length - 1}
              />
            )
          })}
        </ScrollView>
      </View>
    )
  }

  return (
    <FlatList
      ref={ref}
      stickyHeaderIndices={[1]}
      scrollEventThrottle={16}
      decelerationRate={0.8}
      showsVerticalScrollIndicator={false}
      data={items}
      keyExtractor={itemKeys}
      renderItem={renderItem}
      style={styles.itemsContainer}
      contentContainerStyle={styles.itemsContentContainer}
      onScrollEndDrag={onDragEnd}
      onMomentumScrollEnd={onMomentumEnd}
      onContentSizeChange={onContentSizeChange}
      nativeID='scrollId'
    />
  )
}

Catalogue.defaultProps = {
  data: {},
  images: {},
  onOfferPress: () => { },
}

Catalogue.propTypes = {
  data: PropTypes.object,
  images: PropTypes.object,
  onOfferPress: PropTypes.func,
}

export default Catalogue
